/**
 * AddNodeCard - A card component with a plus icon for adding new metric nodes or scopes
 *
 * When clicked, shows a modal to choose the type of node to create.
 * Also supports linking to existing cards from other dashboards.
 */
import { useState, useMemo, useRef, useEffect } from 'react';
import {
  PlusIcon,
  FolderIcon,
  ChartBarIcon,
  ArrowLeftIcon,
  WarningIcon,
  LinkIcon,
  CaretDownIcon,
  CaretRightIcon,
  CheckSquareIcon
} from '@phosphor-icons/react';
import Modal from 'src/components/ui/Modal';
import Button from 'src/components/ui/Button';
import CompoundButton from 'src/components/ui/CompoundButton';
import { InputField } from 'src/components/ui/Input';
import SearchInputField from 'src/components/ui/SearchInput';
import Dropdown from 'src/components/ui/Dropdown';
import Loading from 'src/components/ui/Loading';
import { twMerge } from 'tailwind-merge';
import { useLinkableCards } from '../api/hooks';
import type { DataValueType, AggregationType, BoundaryDirection, LinkableCard } from '../api/types';

type CardTypeFilter = 'all' | 'group_card' | 'data_card' | 'status_card';

type NodeType = 'group_card' | 'data_card' | 'status_card' | 'scope';
type ActionType = NodeType | 'link_existing';

interface CreateNodeData {
  title: string;
  type: NodeType;
  parentId?: string | null;
  linked_node_id?: string;
  data_value_type?: DataValueType;
  aggregation_type?: AggregationType;
  boundary_direction?: BoundaryDirection;
}

interface AddNodeCardProps {
  /** The parent node ID - if null, creates at root level */
  parentId?: string | null;
  /** Root dashboard this card is being added to. Its own root is hidden from the link list. */
  excludeDashboardId?: string | null;
  /** Callback when a node is created */
  onCreate: (data: CreateNodeData) => Promise<void>;
  /** Whether to show scope creation option (for ReportingHome) */
  showScopeOption?: boolean;
  /** Custom class name */
  className?: string;
  /** Label to show on the card */
  label?: string;
  /** Card is the dashed tile. Button is a compact trigger for list layouts. */
  appearance?: 'card' | 'button';
}

const DATA_VALUE_TYPES: { value: DataValueType; label: string }[] = [
  { value: 'percentage', label: 'Percentage (%)' },
  { value: 'number', label: 'Number' }
];

const AGGREGATION_TYPES: { value: AggregationType; label: string }[] = [
  { value: 'average', label: 'Average' },
  { value: 'min', label: 'Minimum' }
];

export default function AddNodeCard({
  parentId = null,
  excludeDashboardId = null,
  onCreate,
  showScopeOption = false,
  className = '',
  label,
  appearance = 'card'
}: AddNodeCardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<ActionType | null>(null);
  const [name, setName] = useState('');
  const [dataValueType, setDataValueType] = useState<DataValueType>('percentage');
  const [aggregationType, setAggregationType] = useState<AggregationType>('average');
  const [selectedLinkedCard, setSelectedLinkedCard] = useState<LinkableCard | null>(null);
  const [linkCardFilter, setLinkCardFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState<CardTypeFilter>('all');
  const [collapsedDashboards, setCollapsedDashboards] = useState<Set<string>>(new Set());
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Track whether the name field has been touched so we can show an inline
  // "Name is required" message instead of just disabling the button with no
  // explanation (the previous "Create does nothing" UX trap).
  const [nameTouched, setNameTouched] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const trimmedName = name.trim();
  const nameError = nameTouched && trimmedName.length === 0 ? 'Name is required.' : null;

  // Fetch linkable cards when the modal is open and link option is selected
  const { data: linkableCardsData, isLoading: isLoadingLinkableCards } = useLinkableCards(
    isModalOpen && selectedAction === 'link_existing'
  );

  // Autofocus the search input when the link panel opens
  useEffect(() => {
    if (selectedAction === 'link_existing' && !isLoadingLinkableCards) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [selectedAction, isLoadingLinkableCards]);

  // Filter and group linkable cards by dashboard
  const groupedLinkableCards = useMemo(() => {
    if (!linkableCardsData?.cards) return new Map<string, LinkableCard[]>();

    const filtered = linkableCardsData.cards.filter((card) => {
      if (excludeDashboardId && card.id === excludeDashboardId) return false;
      if (typeFilter !== 'all' && card.type !== typeFilter) return false;
      if (!linkCardFilter) return true;
      const query = linkCardFilter.toLowerCase();
      return (
        card.title.toLowerCase().includes(query) ||
        card.dashboard_name.toLowerCase().includes(query) ||
        card.path.toLowerCase().includes(query)
      );
    });

    const grouped = new Map<string, LinkableCard[]>();
    for (const card of filtered) {
      const existing = grouped.get(card.dashboard_id) || [];
      existing.push(card);
      grouped.set(card.dashboard_id, existing);
    }
    return grouped;
  }, [linkableCardsData, linkCardFilter, typeFilter, excludeDashboardId]);

  const totalFilteredCount = useMemo(
    () => Array.from(groupedLinkableCards.values()).reduce((sum, cards) => sum + cards.length, 0),
    [groupedLinkableCards]
  );

  const toggleDashboardCollapsed = (dashboardId: string) => {
    setCollapsedDashboards((prev) => {
      const next = new Set(prev);
      if (next.has(dashboardId)) {
        next.delete(dashboardId);
      } else {
        next.add(dashboardId);
      }
      return next;
    });
  };

  const handleCardClick = () => {
    setIsModalOpen(true);
    setSelectedAction(null);
    setName('');
    setDataValueType('percentage');
    setAggregationType('average');
    setSelectedLinkedCard(null);
    setLinkCardFilter('');
    setTypeFilter('all');
    setCollapsedDashboards(new Set());
    setError(null);
    setNameTouched(false);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setSelectedAction(null);
    setName('');
    setSelectedLinkedCard(null);
    setLinkCardFilter('');
    setTypeFilter('all');
    setCollapsedDashboards(new Set());
    setError(null);
    setNameTouched(false);
  };

  const handleCreate = async () => {
    // For linking, we need a selected card and a name
    if (selectedAction === 'link_existing') {
      if (!selectedLinkedCard) {
        setError('Please select a card to link');
        return;
      }
      if (!name.trim()) {
        setNameTouched(true);
        setError('Please enter a name for the linked card');
        return;
      }
    } else if (!selectedAction || !name.trim()) {
      setNameTouched(true);
      setError('Please enter a name');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      let data: CreateNodeData;

      if (selectedAction === 'link_existing' && selectedLinkedCard) {
        // Create a linked card
        data = {
          title: name.trim(),
          type: selectedLinkedCard.type,
          parentId,
          linked_node_id: selectedLinkedCard.id
        };
      } else {
        // Create a regular card
        data = {
          title: name.trim(),
          type: selectedAction as NodeType,
          parentId: selectedAction === 'scope' ? undefined : parentId
        };

        // Add required fields based on type
        if (selectedAction === 'data_card') {
          data.data_value_type = dataValueType;
          data.boundary_direction = 'descending'; // Default to higher is better
        } else if (selectedAction === 'group_card') {
          data.aggregation_type = aggregationType;
        }
      }

      await onCreate(data);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create');
    } finally {
      setIsCreating(false);
    }
  };

  const actionOptions = showScopeOption
    ? [
        {
          action: 'scope' as ActionType,
          icon: ChartBarIcon,
          label: 'New Dashboard',
          description: 'Create a new assurance dashboard'
        }
      ]
    : [
        {
          action: 'group_card' as ActionType,
          icon: FolderIcon,
          label: 'Group Card',
          description: 'A group that aggregates individual data points'
        },
        {
          action: 'data_card' as ActionType,
          icon: ChartBarIcon,
          label: 'Score Card',
          description: 'A single source with a value scored against thresholds'
        },
        {
          action: 'status_card' as ActionType,
          icon: CheckSquareIcon,
          label: 'Status Card',
          description: 'A yes / no check recorded'
        },
        {
          action: 'link_existing' as ActionType,
          icon: LinkIcon,
          label: 'Link Existing Card',
          description: 'Display data from an existing card without copying it'
        }
      ];

  return (
    <>
      {/* Add Card */}
      {appearance === 'button' ? (
        <Button
          type="button"
          variant="primary"
          icon={PlusIcon}
          text={label ?? (showScopeOption ? 'Add Dashboard' : 'Add Card')}
          onClick={handleCardClick}
          className={className}
        />
      ) : (
        <CompoundButton
          size="lg"
          onClick={handleCardClick}
          icon={PlusIcon}
          title={label ?? (showScopeOption ? 'Add Dashboard' : 'Add Card')}
          description={showScopeOption ? 'Create a new assurance dashboard' : 'Add a new card to the dashboard'}
          className={twMerge('w-full border-2 border-dashed', className)}
        />
      )}

      {/* Creation Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleClose}
        title={
          <h2 className="text-base font-medium text-content-default">
            {showScopeOption ? 'Create Dashboard' : 'Add Card'}
          </h2>
        }
        className="!max-w-md p-6 w-full"
      >
        <div className="space-y-6">
          {/* Step 1: Choose Action */}
          {!selectedAction && (
            <div className="space-y-3">
              <p className="text-sm text-content-subtle mb-4">
                {showScopeOption
                  ? 'Create a new assurance dashboard to track compliance.'
                  : 'Choose the type of card to add:'}
              </p>
              {actionOptions.map((option) => (
                <CompoundButton
                  key={option.action}
                  size="lg"
                  onClick={() => setSelectedAction(option.action)}
                  icon={option.icon}
                  title={option.label}
                  description={option.description}
                  className="w-full"
                />
              ))}
            </div>
          )}

          {/* Step 2: Link to existing card */}
          {selectedAction === 'link_existing' && (
            <div className="space-y-4">
              {/* Search and type filter */}
              <div className="flex flex-col gap-3">
                <SearchInputField
                  ref={searchInputRef}
                  value={linkCardFilter}
                  onChange={(e) => setLinkCardFilter(e.target.value)}
                  placeholder="Search by card name, path or dashboard..."
                  fullWidth
                />
                <div className="flex items-center gap-1.5">
                  {(['all', 'group_card', 'data_card', 'status_card'] as const).map((filter) => {
                    const filterLabel =
                      filter === 'all'
                        ? 'All'
                        : filter === 'group_card'
                          ? 'Groups'
                          : filter === 'data_card'
                            ? 'Score Cards'
                            : 'Status Cards';
                    const isActive = typeFilter === filter;
                    return (
                      <Button
                        key={filter}
                        variant={isActive ? 'primary' : 'outline'}
                        size="sm"
                        onClick={() => setTypeFilter(filter)}
                      >
                        {filterLabel}
                      </Button>
                    );
                  })}
                  {(linkCardFilter || typeFilter !== 'all') && (
                    <span className="text-xs text-content-subtle ml-auto">
                      {totalFilteredCount} {totalFilteredCount === 1 ? 'result' : 'results'}
                    </span>
                  )}
                </div>
              </div>

              {/* Card list */}
              <div className="max-h-72 overflow-y-auto border border-border-default rounded-lg">
                {isLoadingLinkableCards ? (
                  <div className="p-4 text-center">
                    <Loading text="Loading available cards..." />
                  </div>
                ) : groupedLinkableCards.size === 0 ? (
                  <div className="p-6 text-center text-content-subtle text-sm">
                    {linkCardFilter || typeFilter !== 'all'
                      ? 'No cards match your search. Try adjusting your filters.'
                      : 'No linkable cards available.'}
                  </div>
                ) : (
                  <div className="divide-y divide-border-default">
                    {Array.from(groupedLinkableCards.entries()).map(([id, cards]) => {
                      const isCollapsed = collapsedDashboards.has(id);
                      return (
                        <div key={id}>
                          <button
                            type="button"
                            onClick={() => toggleDashboardCollapsed(id)}
                            className="w-full px-3 py-2 bg-gray-50 hover:bg-gray-100 flex items-center gap-1.5 transition-colors"
                          >
                            {isCollapsed ? (
                              <CaretRightIcon size={12} className="text-content-subtle flex-shrink-0" />
                            ) : (
                              <CaretDownIcon size={12} className="text-content-subtle flex-shrink-0" />
                            )}
                            <span className="text-xs font-medium text-content-subtle">{cards[0]?.dashboard_name}</span>
                            <span className="text-xs text-content-subtlest ml-auto">{cards.length}</span>
                          </button>
                          {!isCollapsed &&
                            cards.map((card) => (
                              <button
                                key={card.id}
                                type="button"
                                onClick={() => {
                                  setSelectedLinkedCard(card);
                                  if (!name) setName(card.title);
                                }}
                                className={`w-full px-3 py-2 text-left hover:bg-gray-100 flex items-start gap-2 transition-colors ${
                                  selectedLinkedCard?.id === card.id
                                    ? 'bg-blue-50 border-l-2 border-blue-500'
                                    : 'border-l-2 border-transparent'
                                }`}
                              >
                                {card.type === 'group_card' ? (
                                  <FolderIcon size={16} className="text-content-subtle flex-shrink-0 mt-0.5" />
                                ) : card.type === 'status_card' ? (
                                  <CheckSquareIcon size={16} className="text-content-subtle flex-shrink-0 mt-0.5" />
                                ) : (
                                  <ChartBarIcon size={16} className="text-content-subtle flex-shrink-0 mt-0.5" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium truncate">{card.title}</div>
                                  <div className="text-xs text-content-subtle truncate">
                                    {card.id === card.dashboard_id ? 'Whole dashboard' : card.path}
                                  </div>
                                </div>
                                <span className="text-xs text-content-subtle ml-auto flex-shrink-0">
                                  {card.id === card.dashboard_id
                                    ? 'Dashboard'
                                    : card.type === 'group_card'
                                      ? 'Group'
                                      : card.type === 'status_card'
                                        ? 'Status'
                                        : 'Score'}
                                </span>
                              </button>
                            ))}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Name for linked card */}
              {selectedLinkedCard && (
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-content-subtle">Display Name</label>
                  <InputField
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (!nameTouched) setNameTouched(true);
                    }}
                    onBlur={() => setNameTouched(true)}
                    placeholder="Name for this linked card"
                    fullWidth
                    state={error || nameError ? 'error' : 'default'}
                  />
                  {nameError && <p className="text-xs text-misc-error">{nameError}</p>}
                  <p className="text-xs text-content-subtle">
                    This card will display data from &ldquo;{selectedLinkedCard.title}&rdquo;. Changes to the original
                    will be reflected here.
                  </p>
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 text-misc-error text-sm">
                  <WarningIcon size={20} className="flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex justify-between pt-2">
                <Button variant="ghost" icon={ArrowLeftIcon} onClick={() => setSelectedAction(null)}>
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="ghost" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreate}
                    disabled={isCreating || !selectedLinkedCard || !name.trim()}
                    loading={isCreating}
                  >
                    Link Card
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Enter Details (for new cards) */}
          {selectedAction && selectedAction !== 'link_existing' && (
            <div className="space-y-4">
              {/* Name Input */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-content-subtle">
                  {selectedAction === 'scope'
                    ? 'Dashboard Name'
                    : selectedAction === 'group_card'
                      ? 'Group Name'
                      : selectedAction === 'status_card'
                        ? 'Status Check Name'
                        : 'Metric Name'}
                </label>
                <InputField
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (!nameTouched) setNameTouched(true);
                  }}
                  onBlur={() => setNameTouched(true)}
                  placeholder={
                    selectedAction === 'scope'
                      ? 'e.g., Health & Safety Assurance'
                      : selectedAction === 'group_card'
                        ? 'e.g., Incidents & Accidents'
                        : selectedAction === 'status_card'
                          ? 'e.g., Monthly Audit Completed'
                          : 'e.g., Violence Incidents'
                  }
                  fullWidth
                  state={error || nameError ? 'error' : 'default'}
                  autoFocus
                />
                {nameError && <p className="text-xs text-misc-error">{nameError}</p>}
              </div>

              {/* Data Card specific: Data Value Type */}
              {selectedAction === 'data_card' && (
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-content-subtle">Data Type</label>
                  <Dropdown
                    options={DATA_VALUE_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                    initialValue={dataValueType}
                    onChange={(v) => setDataValueType(v as DataValueType)}
                    width="w-full"
                  />
                  <p className="text-xs text-content-subtle">Choose what type of data you have for this card</p>
                </div>
              )}

              {/* Group Card specific: Aggregation Type */}
              {selectedAction === 'group_card' && (
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-content-subtle">Aggregation Method</label>
                  <Dropdown
                    options={AGGREGATION_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                    initialValue={aggregationType}
                    onChange={(v) => setAggregationType(v as AggregationType)}
                    width="w-full"
                  />
                  <p className="text-xs text-content-subtle">
                    How individual data points will be combined to calculate this group's assurance score.
                    <br />
                    <br />
                    <span className="text-xs text-content-subtle">
                      <strong>Note:</strong> Group cards don't have their own data. Their assurance level is calculated
                      by aggregating its cards using the selected method.
                    </span>
                    <br />
                    <br />
                    <ul className="list-disc pl-5 text-xs text-content-subtle">
                      <li>
                        <strong>Minimum:</strong> The lowest value of the individual data points will be used to
                        calculate the group's assurance score.
                      </li>
                      <li>
                        <strong>Average:</strong> The average of the individual data points will be used to calculate
                        the group's assurance score.
                      </li>
                    </ul>
                  </p>
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 text-misc-error text-sm">
                  <WarningIcon size={20} className="flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex justify-between pt-2">
                <Button variant="ghost" icon={ArrowLeftIcon} onClick={() => setSelectedAction(null)}>
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="ghost" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreate} disabled={isCreating || !name.trim()} loading={isCreating}>
                    Create
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
